import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  FormHelperText,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';
import LocationAutocomplete from '../../components/LocationAutocomplete';

// Esquema de validación
const ClientSchema = Yup.object().shape({
  document_type: Yup.string().required('El tipo de documento es requerido'),
  document_number: Yup.string()
    .required('El número de documento es requerido')
    .when('document_type', {
      is: 'DNI',
      then: (schema) => schema.matches(/^\d{8}$/, 'El DNI debe tener 8 dígitos')
    }),
  first_name: Yup.string().required('El nombre es requerido'),
  father_last_name: Yup.string().required('El apellido paterno es requerido'),
  mother_last_name: Yup.string().required('El apellido materno es requerido'),
  phone_prefix: Yup.string().required('El prefijo telefónico es requerido'),
  phone_number: Yup.string()
    .required('El número telefónico es requerido')
    .matches(/^\d{9}$/, 'El número telefónico debe tener 9 dígitos'),
  location_ubigeo: Yup.number().required('La ubicación es requerida')
});

const ClientForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Valores iniciales para el formulario
  const initialValues = {
    document_type: 'DNI',
    document_number: '',
    first_name: '',
    father_last_name: '',
    mother_last_name: '',
    phone_prefix: '+51',
    phone_number: '',
    location_ubigeo: '',
  };

  // Cargar ubicaciones
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const data = await apiService.getLocationUbigeos();
        setLocations(data);
      } catch (err) {
        console.error('Error al cargar ubicaciones:', err);
        setError('Error al cargar las ubicaciones. Por favor, intente nuevamente.');
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Cargar datos del cliente si estamos en modo edición
  useEffect(() => {
    if (isEditMode) {
      const fetchClient = async () => {
        try {
          setLoading(true);
          const data = await apiService.getClient(id);
          setClient(data);
        } catch (err) {
          console.error('Error al cargar cliente:', err);
          setError('Error al cargar los datos del cliente. Por favor, intente nuevamente.');
        } finally {
          setLoading(false);
        }
      };

      fetchClient();
    }
  }, [id, isEditMode]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEditMode) {
        await apiService.updateClient(id, values);
      } else {
        await apiService.createClient(values);
      }
      navigate('/clients');
    } catch (err) {
      console.error('Error al guardar cliente:', err);
      setError('Error al guardar los datos del cliente. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  if (loading || loadingLocations) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Editar Cliente' : 'Nuevo Cliente'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={client || initialValues}
          validationSchema={ClientSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting, values, handleChange, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información Personal
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth error={touched.document_type && Boolean(errors.document_type)}>
                    <InputLabel id="document-type-label">Tipo de Documento</InputLabel>
                    <Field
                      as={Select}
                      name="document_type"
                      labelId="document-type-label"
                      label="Tipo de Documento"
                      value={values.document_type}
                      onChange={(e) => {
                        handleChange(e);
                        // Limpiar el número de documento si cambia el tipo
                        if (e.target.value !== values.document_type) {
                          setFieldValue('document_number', '');
                        }
                      }}
                    >
                      <MenuItem value="DNI">DNI</MenuItem>
                      <MenuItem value="CE">Carnet de Extranjería</MenuItem>
                      <MenuItem value="PAS">Pasaporte</MenuItem>
                    </Field>
                    {touched.document_type && errors.document_type && (
                      <FormHelperText>{errors.document_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Field
                    as={TextField}
                    name="document_number"
                    label="Número de Documento"
                    fullWidth
                    variant="outlined"
                    error={touched.document_number && Boolean(errors.document_number)}
                    helperText={touched.document_number && errors.document_number}
                    inputProps={{
                      maxLength: values.document_type === 'DNI' ? 8 : 15
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="first_name"
                    label="Nombres"
                    fullWidth
                    variant="outlined"
                    error={touched.first_name && Boolean(errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="father_last_name"
                    label="Apellido Paterno"
                    fullWidth
                    variant="outlined"
                    error={touched.father_last_name && Boolean(errors.father_last_name)}
                    helperText={touched.father_last_name && errors.father_last_name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="mother_last_name"
                    label="Apellido Materno"
                    fullWidth
                    variant="outlined"
                    error={touched.mother_last_name && Boolean(errors.mother_last_name)}
                    helperText={touched.mother_last_name && errors.mother_last_name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de Contacto
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    as={TextField}
                    name="phone_prefix"
                    label="Prefijo Telefónico"
                    fullWidth
                    variant="outlined"
                    error={touched.phone_prefix && Boolean(errors.phone_prefix)}
                    helperText={touched.phone_prefix && errors.phone_prefix}
                  />
                </Grid>

                <Grid item xs={12} md={9}>
                  <Field
                    as={TextField}
                    name="phone_number"
                    label="Número Telefónico"
                    fullWidth
                    variant="outlined"
                    error={touched.phone_number && Boolean(errors.phone_number)}
                    helperText={touched.phone_number && errors.phone_number}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LocationAutocomplete
                    locations={locations}
                    value={values.location_ubigeo}
                    onChange={(value) => setFieldValue('location_ubigeo', value)}
                    error={touched.location_ubigeo && Boolean(errors.location_ubigeo)}
                    helperText={touched.location_ubigeo && errors.location_ubigeo}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ClientForm; 